'use client';

import React, { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';

interface ModuleCardItem {
  num: string;
  title: string;
  body: string;
  cta: string;
  href: string;
  img: string;
  alt: string;
  bgGradient: string;
}

interface ModuleCardsProps {
  locale: string;
  cards: ModuleCardItem[];
}

export function ModuleCards({ locale, cards }: ModuleCardsProps) {
  const tiltRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>, index: number) => {
    const el = tiltRefs.current[index];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `perspective(1000px) rotateY(${x * 12}deg) rotateX(${-y * 12}deg) translateZ(8px)`;
  };

  const handleMouseLeave = (index: number) => {
    const el = tiltRefs.current[index];
    if (el) {
      el.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)';
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
      {cards.map((card, idx) => (
        <div key={card.num} className="relative group h-full flex flex-col">
          {/* Subtle back decorative glow/offset border */}
          <div
            className={`absolute inset-0 rounded-[26px] border border-white/20 bg-gradient-to-br from-white/10 to-white/[0.02] pointer-events-none ${
              idx === 0 ? '-rotate-2 translate-x-[-4px] translate-y-[6px]' : idx === 1 ? 'rotate-2 translate-x-[4px] translate-y-[6px]' : '-rotate-1 translate-y-[6px]'
            }`}
          />
          <Link
            ref={(el) => { tiltRefs.current[idx] = el; }}
            href={card.href}
            onMouseMove={(e) => handleMouseMove(e, idx)}
            onMouseLeave={() => handleMouseLeave(idx)}
            className="relative flex flex-col items-center justify-between text-center p-6 sm:p-7 h-full min-h-[380px] sm:min-h-[410px] rounded-[26px] overflow-hidden border border-white/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.6),inset_0_0_40px_rgba(150,165,255,0.22),0_30px_60px_-20px_rgba(0,30,100,0.8)] transition-transform duration-200 ease-out"
            style={{ background: card.bgGradient }}
          >
            {/* Background image illustration */}
            <div className="absolute inset-0 z-0">
              <Image
                src={card.img}
                alt=""
                fill
                loading="lazy"
                sizes="(max-width: 768px) 100vw, 33vw"
                className="object-cover object-center opacity-85 group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(4,10,40,0.92)] via-[rgba(4,10,40,0.4)] to-transparent pointer-events-none" />
            </div>

            {/* Spacer */}
            <div className="h-28 sm:h-32 relative z-10" />

            {/* Content anchored to bottom */}
            <div className="relative z-10 mt-auto flex flex-col items-center gap-2 w-full">
              <h3 className="font-heading font-medium text-lg sm:text-[21px] leading-snug text-white drop-shadow-[0_0_16px_rgba(150,165,255,0.7)] line-clamp-2 min-h-[2.8rem] flex items-center justify-center">
                {card.title}
              </h3>
              <p className="text-xs sm:text-[13px] text-[rgba(230,242,255,0.85)] leading-relaxed max-w-[280px] line-clamp-3 min-h-[3.3rem]">
                {card.body}
              </p>
              <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-white">
                <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-[11px] font-mono">
                  {card.num}
                </span>
                <span>{card.cta} →</span>
              </div>
            </div>
          </Link>
        </div>
      ))}
    </div>
  );
}
